import { MenuItem, Stack, TextField } from '@mui/material';
import { useMe } from '../../api/queries';
import { useRevenueScope } from '../../api/revenue';

/** Selected operator/site drill filter — `null` means "All operators" /
 *  "All sites" respectively. Mirrors the `operatorId`/`siteId` query params
 *  `useRevenuePortfolio` accepts directly. */
export type RevenueScopeFilterValue = { operatorId: string | null; siteId: string | null };

export const ALL_OPERATORS_SCOPE: RevenueScopeFilterValue = { operatorId: null, siteId: null };

/**
 * Revenue page filter bar — Operator + Site dropdowns (owner request: "add
 * operator drop down; if enabled add site drop down as well").
 *
 * Data source is `useRevenueScope()` (GET /api/v1/revenue/scope), which
 * returns exactly the operators/sites the caller can see under RLS — the
 * SAME visibility rule the portfolio numbers themselves obey, so the
 * dropdown options are never wider than what filtering by them can actually
 * return data for.
 *
 * Visibility (hide, don't disable — see house rule "hide actions the user
 * lacks permission for, don't render+disable" in PORTAL_FORM_RULES §11.1,
 * applied here to "nothing to choose" rather than "no permission"):
 *   - Operator dropdown renders only when there's more than one operator to
 *     choose from. A caller who can only ever see their own operator (the
 *     common case — an operator-scoped user) gets a single-row `operators`
 *     list; a dropdown with exactly one always-selected, never-changeable
 *     option adds visual clutter and an implicit "why can't I change this"
 *     question without ever doing anything useful. Disabling it instead
 *     would keep the clutter without adding any capability, so it's hidden.
 *   - Site dropdown renders only when there's more than one RLS-visible site
 *     in total (not just within the currently-selected operator) — so it
 *     doesn't pop in and out of the layout as the operator selection
 *     changes; the OPTIONS it offers do still cascade to the selected
 *     operator (or every visible site under "All operators").
 *
 * Cascade + reset: picking an operator resets the site selection back to
 * "All sites" (a previously-selected site could belong to a different
 * operator) and narrows the site dropdown's options to that operator's own
 * sites. Picking "All operators" restores every RLS-visible site as options.
 */
export function RevenueScopeFilters({ value, onChange }: {
  value: RevenueScopeFilterValue;
  onChange: (next: RevenueScopeFilterValue) => void;
}) {
  const me = useMe();
  const scopeQ = useRevenueScope();
  const operators = scopeQ.data?.operators ?? [];
  const allSites = scopeQ.data?.sites ?? [];

  // ARMS-only affordance (owner ruling): operator-scoped users — and ARMS
  // users while impersonating one — are already scoped automatically by the
  // DB role system (RLS), so drill dropdowns are noise there. Same effective-
  // ARMS test the shell uses for its ARMS-only controls.
  const isRealArms = !!me.data?.is_arms && !me.data?.impersonated_operator_id;

  const showOperatorFilter = operators.length > 1;
  const showSiteFilter = allSites.length > 1;

  if (!isRealArms || (!showOperatorFilter && !showSiteFilter)) return null;

  const sitesInScope = value.operatorId
    ? allSites.filter((s) => s.operatorId === value.operatorId)
    : allSites;

  // Defensive display fallback: if the selected siteId isn't among the
  // currently-cascaded options (e.g. the scope list just changed underneath
  // an in-flight selection), fall back to "All sites" rather than handing
  // MUI's <Select> an out-of-range value (which renders a console warning
  // and an empty-looking control).
  const siteSelectValue = value.siteId && sitesInScope.some((s) => s.siteId === value.siteId)
    ? value.siteId
    : '';

  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap">
      {showOperatorFilter && (
        <TextField
          select
          label="Operator"
          size="small"
          sx={{ minWidth: 200 }}
          value={value.operatorId ?? ''}
          // MUI's <Select> renders the field BLANK for an empty-string value
          // unless displayEmpty is set — without it, "All operators" (the
          // default state) would render as a confusingly empty box instead
          // of showing its own label. displayEmpty puts text in the field
          // while the value is still '', which the floating label doesn't
          // know about — so it must be pinned shrunk or it overlays the
          // "All operators" text until first interaction.
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          onChange={(e) => {
            const operatorId = e.target.value || null;
            // Changing operator always resets the site selection — a
            // previously-picked site may not belong to the new operator.
            onChange({ operatorId, siteId: null });
          }}
        >
          <MenuItem value="">All operators</MenuItem>
          {operators.map((op) => (
            <MenuItem key={op.operatorId} value={op.operatorId}>{op.displayName}</MenuItem>
          ))}
        </TextField>
      )}

      {showSiteFilter && (
        <TextField
          select
          label="Site"
          size="small"
          sx={{ minWidth: 200 }}
          value={siteSelectValue}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          onChange={(e) => onChange({ ...value, siteId: e.target.value || null })}
        >
          <MenuItem value="">All sites</MenuItem>
          {sitesInScope.map((s) => (
            <MenuItem key={s.siteId} value={s.siteId}>{s.siteName}</MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}
